"use client";

import { c as _c } from "react/compiler-runtime";
import { buildCookieConsentDocumentValue, createAcceptedCookieConsentSelection, createRejectedCookieConsentSelection, createStoredCookieConsentState, isConsentVendorAllowed, serializeCookieConsentValue } from "@multi-tenant-cms/seo-runtime";
import type { CookieConsentCategorySelection, CookieConsentConfig } from "@multi-tenant-cms/types";
import { CookieConsentSurface } from "@multi-tenant-cms/ui/components/cookie-consent-surface";
import Script from "next/script";
import { useState } from "react";
import { HeatmapBootstrap } from "@/components/heatmap-bootstrap";
import { VisitorIntelligenceBootstrap } from "@/components/visitor-intelligence-bootstrap";
import type { TenantHeatmapConfig } from "@/lib/heatmap";
import type { TenantVisitorIntelligenceConfig } from "@/lib/visitor-intelligence";
type Props = {
  analytics: {
    ga4MeasurementId: string | null;
    gtmContainerId: string | null;
    microsoftClarityProjectId: string | null;
    publishMarketingScripts: boolean;
  };
  consentConfig: CookieConsentConfig;
  initialSelection: CookieConsentCategorySelection;
  initialShowBanner: boolean;
  heatmap: TenantHeatmapConfig;
  visitorIntelligence: TenantVisitorIntelligenceConfig;
};
async function writeConsentCookie(config: CookieConsentConfig, selection: CookieConsentCategorySelection, status: "accepted_all" | "customized" | "rejected_non_essential") {
  const storedState = createStoredCookieConsentState(config, status, selection);
  const cookieStore = (window as Window & {
    cookieStore?: {
      set: (options: {
        expires?: number | Date;
        name: string;
        path?: string;
        sameSite?: "lax" | "strict" | "none";
        secure?: boolean;
        value: string;
      }) => Promise<void>;
    };
  }).cookieStore;
  const secure = window.location.protocol === "https:";
  const serializedValue = serializeCookieConsentValue(storedState);
  if (cookieStore?.set) {
    try {
      await cookieStore.set({
        expires: Date.now() + config.cookie_max_age_days * 24 * 60 * 60 * 1000,
        name: config.cookie_name,
        path: "/",
        sameSite: "lax",
        secure,
        value: serializedValue
      });
      return;
    } catch {
      // Fall through to `document.cookie` for browsers without a working Cookie Store API.
    }
  }

  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not available in all target browsers.
  document.cookie = buildCookieConsentDocumentValue(config, storedState, {
    secure
  });
}
export function CookieConsentClient(t0) {
  const $ = _c(52);
  const {
    analytics,
    consentConfig,
    initialSelection,
    initialShowBanner,
    heatmap,
    visitorIntelligence
  } = t0;
  const [selection, setSelection] = useState(initialSelection);
  const [showBanner, setShowBanner] = useState(initialShowBanner);
  let t1;
  if ($[0] !== analytics.gtmContainerId || $[1] !== analytics.publishMarketingScripts || $[2] !== consentConfig || $[3] !== selection) {
    t1 = analytics.publishMarketingScripts && Boolean(analytics.gtmContainerId) && isConsentVendorAllowed(consentConfig, selection, "gtm");
    $[0] = analytics.gtmContainerId;
    $[1] = analytics.publishMarketingScripts;
    $[2] = consentConfig;
    $[3] = selection;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const gtmAllowed = t1;
  let t2;
  if ($[5] !== analytics.ga4MeasurementId || $[6] !== analytics.gtmContainerId || $[7] !== analytics.publishMarketingScripts || $[8] !== consentConfig || $[9] !== gtmAllowed || $[10] !== selection) {
    t2 = analytics.publishMarketingScripts && Boolean(analytics.ga4MeasurementId) && isConsentVendorAllowed(consentConfig, selection, "ga4") && !(analytics.gtmContainerId && gtmAllowed);
    $[5] = analytics.ga4MeasurementId;
    $[6] = analytics.gtmContainerId;
    $[7] = analytics.publishMarketingScripts;
    $[8] = consentConfig;
    $[9] = gtmAllowed;
    $[10] = selection;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const ga4Allowed = t2;
  let t3;
  if ($[12] !== analytics.microsoftClarityProjectId || $[13] !== analytics.publishMarketingScripts || $[14] !== consentConfig || $[15] !== selection) {
    t3 = analytics.publishMarketingScripts && Boolean(analytics.microsoftClarityProjectId) && isConsentVendorAllowed(consentConfig, selection, "microsoft_clarity");
    $[12] = analytics.microsoftClarityProjectId;
    $[13] = analytics.publishMarketingScripts;
    $[14] = consentConfig;
    $[15] = selection;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const clarityAllowed = t3;
  let t4;
  let t5;
  let t6;
  if ($[17] !== consentConfig) {
    t4 = () => {
      const nextSelection = createAcceptedCookieConsentSelection(consentConfig);
      setSelection(nextSelection);
      setShowBanner(false);
      writeConsentCookie(consentConfig, nextSelection, "accepted_all").catch(_temp);
    };
    t5 = () => {
      const nextSelection_0 = createRejectedCookieConsentSelection(consentConfig);
      setSelection(nextSelection_0);
      setShowBanner(false);
      writeConsentCookie(consentConfig, nextSelection_0, "rejected_non_essential").catch(_temp2);
    };
    t6 = nextSelection_1 => {
      setSelection(nextSelection_1);
      setShowBanner(false);
      writeConsentCookie(consentConfig, nextSelection_1, "customized").catch(_temp3);
    };
    $[17] = consentConfig;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
  }
  let t7;
  if ($[21] !== consentConfig || $[22] !== selection || $[23] !== showBanner || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t7 = <CookieConsentSurface config={consentConfig} onAcceptAll={t4} onRejectAll={t5} onSavePreferences={t6} selection={selection} showBanner={showBanner} />;
    $[21] = consentConfig;
    $[22] = selection;
    $[23] = showBanner;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== consentConfig || $[29] !== selection || $[30] !== visitorIntelligence) {
    t8 = <VisitorIntelligenceBootstrap config={visitorIntelligence} consentConfig={consentConfig} selection={selection} />;
    $[28] = consentConfig;
    $[29] = selection;
    $[30] = visitorIntelligence;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== consentConfig || $[33] !== heatmap || $[34] !== selection) {
    t9 = <HeatmapBootstrap config={heatmap} consentConfig={consentConfig} selection={selection} />;
    $[32] = consentConfig;
    $[33] = heatmap;
    $[34] = selection;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== analytics.gtmContainerId || $[37] !== gtmAllowed) {
    t10 = gtmAllowed && analytics.gtmContainerId ? <Script id="tenant-gtm" strategy="afterInteractive">{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${analytics.gtmContainerId}');`}</Script> : null;
    $[36] = analytics.gtmContainerId;
    $[37] = gtmAllowed;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== analytics.ga4MeasurementId || $[40] !== ga4Allowed) {
    t11 = ga4Allowed && analytics.ga4MeasurementId ? <><Script src={`https://www.googletagmanager.com/gtag/js?id=${analytics.ga4MeasurementId}`} strategy="afterInteractive" /><Script id="tenant-ga4" strategy="afterInteractive">{`window.dataLayer = window.dataLayer || []; function gtag(){window.dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${analytics.ga4MeasurementId}');`}</Script></> : null;
    $[39] = analytics.ga4MeasurementId;
    $[40] = ga4Allowed;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== analytics.microsoftClarityProjectId || $[43] !== clarityAllowed) {
    t12 = clarityAllowed && analytics.microsoftClarityProjectId ? <Script id="tenant-clarity" strategy="lazyOnload">{`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${analytics.microsoftClarityProjectId}");`}</Script> : null;
    $[42] = analytics.microsoftClarityProjectId;
    $[43] = clarityAllowed;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9) {
    t13 = <>{t7}{t8}{t9}{t10}{t11}{t12}</>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}
function _temp3() {}
function _temp2() {}
function _temp() {}